import { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateEssay } from '../services/api';

export type EssaySaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

interface UseEssayAutoSaveOptions {
  slug: string;
  contentHash?: string | null;
  delay?: number;
}

export function useEssayAutoSave({ slug, contentHash, delay = 1500 }: UseEssayAutoSaveOptions) {
  const [saveStatus, setSaveStatus] = useState<EssaySaveStatus>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ content?: string }>({});
  const hashRef = useRef(contentHash);
  const queryClient = useQueryClient();

  // Keep hash ref up to date
  useEffect(() => {
    hashRef.current = contentHash;
  }, [contentHash]);

  const mutation = useMutation({
    mutationFn: (data: { content: string; expectedHash?: string }) =>
      updateEssay(slug, data),
    onSuccess: (updatedEssay) => {
      setSaveStatus('saved');
      setIsDirty(false);
      hashRef.current = updatedEssay.contentHash ?? null;
      queryClient.invalidateQueries({ queryKey: ['essay', slug] });
      queryClient.invalidateQueries({ queryKey: ['essays'] });
      setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
    },
    onError: (error: Error) => {
      if (error.message.includes('modified externally')) {
        setSaveStatus('conflict');
      } else {
        setSaveStatus('error');
      }
    },
  });

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const data = pendingRef.current;
    if (data.content !== undefined) {
      setSaveStatus('saving');
      mutation.mutate({
        content: data.content,
        expectedHash: hashRef.current ?? undefined,
      });
      pendingRef.current = {};
    }
  }, [mutation]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, delay);
  }, [flush, delay]);

  const handleContentChange = useCallback(
    (content: string) => {
      pendingRef.current.content = content;
      setIsDirty(true);
      setSaveStatus('idle');
      scheduleSave();
    },
    [scheduleSave]
  );

  const saveNow = useCallback(() => {
    flush();
  }, [flush]);

  const forceOverwrite = useCallback(() => {
    // Retry without expectedHash (force overwrite after conflict)
    const data = pendingRef.current;
    if (data.content !== undefined) {
      setSaveStatus('saving');
      mutation.mutate({ content: data.content });
      pendingRef.current = {};
    }
  }, [mutation]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const data = pendingRef.current;
      if (data.content !== undefined) {
        updateEssay(slug, { content: data.content }).catch(() => {});
      }
    };
  }, [slug]);

  return { handleContentChange, saveNow, forceOverwrite, saveStatus, isDirty };
}
